/**
 * Template / archetype flattening and differential extraction (editor round-trip).
 *
 * Uses ADL2 AOM; legacy ADL 1.4 text should be converted first via `parseAdl()` (see ROADMAP Phase 6b for deep migration).
 */

import * as openehr_am from "../../openehr_am.ts";
import * as openehr_base from "../../../base/openehr_base.ts";
import { cloneAttribute, cloneComplexObject } from "../aom_clone.ts";
import {
  applyMergedTerminology,
  buildArchetypeBindingIndex,
  buildArchetypeTermIndex,
  buildMergedTerminology,
  readTemplateId,
} from "../ontology_merge.ts";
import { specializeComplexObject } from "./specialize.ts";

export type { ArchetypeResolver } from "../ontology_merge.ts";
import type { ArchetypeResolver } from "../ontology_merge.ts";
import {
  TERM_ARCHETYPE_SCOPE_KEY,
  TERM_NAME_FALLBACK_NODE_ID_KEY,
  type TermScopeMeta,
} from "../../../generation/term_scope.ts";

function archetypeIdString(arch: openehr_am.ARCHETYPE): string | undefined {
  return arch.archetype_id?.value ?? arch.archetype_id?.toString();
}

/**
 * Flatten an archetype: merge parent chain and resolve external references / slots.
 */
export function flattenArchetypeDefinition(
  archetype: openehr_am.ARCHETYPE,
  resolver: ArchetypeResolver,
  inlined: openehr_am.ARCHETYPE[] = [],
  scopeKeys?: ScopeKeyMap,
): openehr_am.C_COMPLEX_OBJECT | undefined {
  if (!archetype.definition) return undefined;
  const keys = scopeKeys ?? new WeakMap();

  let flat: openehr_am.C_COMPLEX_OBJECT = cloneComplexObject(
    archetype.definition,
  );

  const parentId = archetype.parent_archetype_id?.value ??
    archetype.parent_archetype_id?.toString();
  if (parentId) {
    const parent = resolver.resolve(parentId);
    if (parent?.definition) {
      const parentFlat =
        flattenArchetypeDefinition(parent, resolver, inlined, keys) ??
          parent.definition;
      flat = specializeComplexObject(parentFlat, archetype.definition);
    }
  }

  return resolveSlotsInTree(flat, resolver, inlined, keys);
}

type ScopeKeyMap = WeakMap<openehr_am.ARCHETYPE, Set<string>>;

function noteInlined(
  arch: openehr_am.ARCHETYPE,
  lookupKey: string | undefined,
  inlined: openehr_am.ARCHETYPE[],
  scopeKeys: ScopeKeyMap,
): void {
  inlined.push(arch);
  if (!lookupKey) return;
  let keys = scopeKeys.get(arch);
  if (!keys) {
    keys = new Set();
    scopeKeys.set(arch, keys);
  }
  keys.add(lookupKey);
}

function resolveSlotsInTree(
  root: openehr_am.C_COMPLEX_OBJECT,
  resolver: ArchetypeResolver,
  inlined: openehr_am.ARCHETYPE[],
  scopeKeys: ScopeKeyMap,
): openehr_am.C_COMPLEX_OBJECT {
  const walkObject = (obj: openehr_am.C_OBJECT): openehr_am.C_OBJECT => {
    if (obj instanceof openehr_am.ARCHETYPE_SLOT) {
      return resolveArchetypeSlot(obj, resolver, inlined, scopeKeys);
    }
    if (obj instanceof openehr_am.C_ARCHETYPE_ROOT) {
      return inlineArchetypeRoot(obj, resolver, inlined, scopeKeys);
    }
    if (obj instanceof openehr_am.C_COMPLEX_OBJECT) {
      if (!obj.attributes) return obj;
      for (const attr of obj.attributes) {
        const children =
          (attr as { children?: openehr_am.C_OBJECT[] }).children;
        if (!children) continue;
        (attr as { children: openehr_am.C_OBJECT[] }).children = children.map(
          walkObject,
        );
      }
    }
    return obj;
  };

  if (!root.attributes) return root;
  for (const attr of root.attributes) {
    const children = (attr as { children?: openehr_am.C_OBJECT[] }).children;
    if (!children) continue;
    (attr as { children: openehr_am.C_OBJECT[] }).children = children.map(
      walkObject,
    );
  }
  return root;
}

function archetypeIdFromRef(ref: string | undefined): string | undefined {
  return ref?.trim() || undefined;
}

function tagTermScopeRoot(
  obj: openehr_am.C_OBJECT,
  archetypeId: string,
  nameFallbackNodeId?: string,
): void {
  const meta = obj as TermScopeMeta & openehr_am.C_OBJECT;
  meta[TERM_ARCHETYPE_SCOPE_KEY] = archetypeId;
  // Concept fallback for every inlined root, including specialised use-site
  // ids (`at0039.1`) and template slot ids (`at0.1`). Lookup still prefers the
  // use-site id when that code exists in the scoped bag.
  if (nameFallbackNodeId) {
    meta[TERM_NAME_FALLBACK_NODE_ID_KEY] = nameFallbackNodeId;
  }
}

function tagTermScopeDescendants(
  obj: openehr_am.C_OBJECT,
  archetypeId: string,
): void {
  if (!(obj instanceof openehr_am.C_COMPLEX_OBJECT)) return;
  for (const attr of obj.attributes ?? []) {
    const children = (attr as { children?: openehr_am.C_OBJECT[] }).children;
    if (!children) continue;
    for (const child of children) {
      const meta = child as TermScopeMeta & openehr_am.C_OBJECT;
      meta[TERM_ARCHETYPE_SCOPE_KEY] ??= archetypeId;
      tagTermScopeDescendants(child, archetypeId);
    }
  }
}

/** Tag an inlined archetype: force root scope, inherit-safe scope on descendants. */
function tagInlinedArchetype(
  obj: openehr_am.C_OBJECT,
  archetypeId: string,
  nameFallbackNodeId?: string,
): void {
  tagTermScopeRoot(obj, archetypeId, nameFallbackNodeId);
  tagTermScopeDescendants(obj, archetypeId);
}

function tagTermScopeSubtree(
  obj: openehr_am.C_OBJECT,
  archetypeId: string,
  nameFallbackNodeId?: string,
): void {
  tagTermScopeRoot(obj, archetypeId, nameFallbackNodeId);
  tagTermScopeDescendants(obj, archetypeId);
}

function firstIncludePattern(
  slot: openehr_am.ARCHETYPE_SLOT,
): string | undefined {
  const includes = (slot as {
    includes?: openehr_am.ARCHETYPE_ID_CONSTRAINT[];
  }).includes;
  const first = includes?.[0];
  return first?.constraint?.pattern;
}

function resolveArchetypeSlot(
  slot: openehr_am.ARCHETYPE_SLOT,
  resolver: ArchetypeResolver,
  inlined: openehr_am.ARCHETYPE[],
  scopeKeys: ScopeKeyMap,
): openehr_am.C_OBJECT {
  const pattern = firstIncludePattern(slot);
  if (!pattern) return slot;

  const arch = resolver.resolve(pattern);
  if (!arch?.definition) return slot;

  noteInlined(arch, pattern, inlined, scopeKeys);
  const filler =
    flattenArchetypeDefinition(arch, resolver, inlined, scopeKeys) ??
      arch.definition;
  const result = cloneComplexObject(filler);
  result.node_id = slot.node_id ?? result.node_id;
  result.rm_type_name = slot.rm_type_name ?? result.rm_type_name;
  if (slot.occurrences) result.occurrences = slot.occurrences;
  const archId = archetypeIdString(arch) ?? pattern;
  tagInlinedArchetype(result, archId, arch.definition?.node_id);
  return result;
}

function inlineArchetypeRoot(
  root: openehr_am.C_ARCHETYPE_ROOT,
  resolver: ArchetypeResolver,
  inlined: openehr_am.ARCHETYPE[],
  scopeKeys: ScopeKeyMap,
): openehr_am.C_OBJECT {
  const ref = root.archetype_ref;
  if (!ref) return root;

  const arch = resolver.resolve(ref);
  if (!arch?.definition) return root;

  noteInlined(arch, ref, inlined, scopeKeys);
  const filler =
    flattenArchetypeDefinition(arch, resolver, inlined, scopeKeys) ??
      arch.definition;
  const result = cloneComplexObject(filler);
  result.node_id = root.node_id ?? result.node_id;
  result.rm_type_name = root.rm_type_name ?? result.rm_type_name;
  if (root.occurrences) result.occurrences = root.occurrences;
  if (root.attributes?.length) {
    const specialized = specializeComplexObject(result, root);
    const archId = archetypeIdFromRef(ref) ?? archetypeIdString(arch);
    if (archId) {
      tagInlinedArchetype(specialized, archId, arch.definition?.node_id);
    }
    return specialized;
  }
  const archId = archetypeIdFromRef(ref) ?? archetypeIdString(arch);
  if (archId) tagInlinedArchetype(result, archId, arch.definition?.node_id);
  return result;
}

/**
 * Build an operational template from a source template or archetype.
 */
export function flattenToOperationalTemplate(
  source: openehr_am.TEMPLATE | openehr_am.ARCHETYPE,
  resolver: ArchetypeResolver,
): openehr_am.OPERATIONAL_TEMPLATE {
  const opt = new openehr_am.OPERATIONAL_TEMPLATE();

  opt.archetype_id = source.archetype_id;
  opt.uid = source.uid;
  opt.concept = source.concept;
  opt.parent_archetype_id = source.parent_archetype_id;
  opt.original_language = source.original_language;
  opt.description = source.description;
  opt.ontology = source.ontology;
  opt.adl_version = source.adl_version ?? "2.0";
  opt.rm_release = source.rm_release;
  opt.is_generated = true;

  const inlinedArchetypes: openehr_am.ARCHETYPE[] = [];
  const scopeKeys: ScopeKeyMap = new WeakMap();
  if (source instanceof openehr_am.TEMPLATE) {
    const templateId = readTemplateId(source);
    if (templateId) opt.template_id = templateId;
    opt.definition = source.definition
      ? resolveSlotsInTree(
        cloneComplexObject(source.definition),
        resolver,
        inlinedArchetypes,
        scopeKeys,
      )
      : undefined;
  } else {
    opt.definition = flattenArchetypeDefinition(
      source,
      resolver,
      inlinedArchetypes,
      scopeKeys,
    );
  }

  applyMergedTerminology(
    opt,
    buildMergedTerminology(source, resolver, inlinedArchetypes),
  );
  // Scope the root to its own archetype so at0000 does not suffix-match a
  // nested template's at0000.1 in the flat ontology. Inlined nodes already
  // carry their template-id scope and are left in place.
  const rootScope = archetypeIdString(source);
  if (rootScope && opt.definition) {
    tagTermScopeRoot(opt.definition, rootScope);
    tagTermScopeDescendants(opt.definition, rootScope);
    let rootKeys = scopeKeys.get(source);
    if (!rootKeys) {
      rootKeys = new Set();
      scopeKeys.set(source, rootKeys);
    }
    rootKeys.add(rootScope);
    const templateId = readTemplateId(source);
    if (templateId) rootKeys.add(templateId);
  }
  (opt as {
    archetype_term_definitions?: ReturnType<typeof buildArchetypeTermIndex>;
  })
    .archetype_term_definitions = buildArchetypeTermIndex(
      resolver,
      [source, ...inlinedArchetypes],
      scopeKeys,
    );
  (opt as {
    archetype_term_bindings?: ReturnType<typeof buildArchetypeBindingIndex>;
  })
    .archetype_term_bindings = buildArchetypeBindingIndex([
      source,
      ...inlinedArchetypes,
    ], scopeKeys);

  return opt;
}

function multiplicityEqual(
  a: openehr_base.Multiplicity_interval | undefined,
  b: openehr_base.Multiplicity_interval | undefined,
): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.lower === b.lower && a.upper === b.upper;
}

function objectsStructurallyEqual(
  a: openehr_am.C_OBJECT,
  b: openehr_am.C_OBJECT,
): boolean {
  if (a.rm_type_name !== b.rm_type_name || a.node_id !== b.node_id) {
    return false;
  }
  if (!multiplicityEqual(a.occurrences, b.occurrences)) return false;

  if (
    a instanceof openehr_am.C_COMPLEX_OBJECT &&
    b instanceof openehr_am.C_COMPLEX_OBJECT
  ) {
    const attrsA = a.attributes ?? [];
    const attrsB = b.attributes ?? [];
    if (attrsA.length !== attrsB.length) return false;
    for (const attrA of attrsA) {
      const attrB = attrsB.find(
        (x) => x.rm_attribute_name === attrA.rm_attribute_name,
      );
      if (!attrB) return false;
      const chA = (attrA as { children?: openehr_am.C_OBJECT[] }).children ??
        [];
      const chB = (attrB as { children?: openehr_am.C_OBJECT[] }).children ??
        [];
      if (chA.length !== chB.length) return false;
      for (let i = 0; i < chA.length; i++) {
        if (!objectsStructurallyEqual(chA[i], chB[i])) return false;
      }
    }
    return true;
  }

  return a.constructor.name === b.constructor.name;
}

/**
 * Extract template overlay / differential constraints vs a flat parent definition.
 * Used when saving editor changes back to a TEMPLATE (bidirectional path).
 */
export function extractDifferentialDefinition(
  flat: openehr_am.C_COMPLEX_OBJECT,
  parentFlat: openehr_am.C_COMPLEX_OBJECT,
): openehr_am.C_COMPLEX_OBJECT | undefined {
  const diff = new openehr_am.C_COMPLEX_OBJECT();
  diff.rm_type_name = flat.rm_type_name;
  diff.node_id = flat.node_id;

  let hasContent = false;

  if (!multiplicityEqual(flat.occurrences, parentFlat.occurrences)) {
    diff.occurrences = flat.occurrences;
    hasContent = true;
  }

  const diffAttrs: openehr_am.C_ATTRIBUTE[] = [];

  for (const flatAttr of flat.attributes ?? []) {
    const name = flatAttr.rm_attribute_name;
    if (!name) continue;
    const parentAttr = parentFlat.attributes?.find(
      (a) => a.rm_attribute_name === name,
    );
    if (!parentAttr) {
      diffAttrs.push(cloneAttribute(flatAttr));
      hasContent = true;
      continue;
    }

    const flatChildren =
      (flatAttr as { children?: openehr_am.C_OBJECT[] }).children ?? [];
    const parentChildren =
      (parentAttr as { children?: openehr_am.C_OBJECT[] }).children ?? [];

    const childDiffs: openehr_am.C_OBJECT[] = [];
    for (const flatChild of flatChildren) {
      const match = parentChildren.find(
        (p) =>
          p.node_id === flatChild.node_id &&
          p.rm_type_name === flatChild.rm_type_name,
      );
      if (!match) {
        childDiffs.push(
          cloneComplexObject(flatChild as openehr_am.C_COMPLEX_OBJECT),
        );
        continue;
      }
      if (
        flatChild instanceof openehr_am.C_COMPLEX_OBJECT &&
        match instanceof openehr_am.C_COMPLEX_OBJECT &&
        !objectsStructurallyEqual(flatChild, match)
      ) {
        const nested = extractDifferentialDefinition(flatChild, match);
        if (nested) childDiffs.push(nested);
      }
    }

    if (childDiffs.length) {
      const attrClone = cloneAttribute(flatAttr);
      (attrClone as { children: openehr_am.C_OBJECT[] }).children = childDiffs;
      diffAttrs.push(attrClone);
      hasContent = true;
    }
  }

  if (!hasContent) return undefined;
  diff.attributes = diffAttrs;
  return diff;
}
